import type { TExperienceMode } from "@lexicanon/shared"
import { msg } from "@lingui/core/macro"
import { useLingui } from "@lingui/react"
import { useMutation } from "@tanstack/react-query"
import { useCallback, useEffect, useState } from "react"
import { updateExperienceMode } from "../lib/api/experience-mode"
import { refreshSession } from "../lib/auth-client"
import { resolveExperienceMode, setExperienceModeLocal, subscribeExperienceMode } from "../lib/experience-mode-store"
import { notifyError } from "../lib/notify"

interface IUseExperienceMode {
  current: TExperienceMode
  setMode: (mode: TExperienceMode) => void
  isPending: boolean
}

/**
 * Persist the user's default for new meetings (experience_mode). Optimistic
 * localStorage so chrome flips before the Better Auth session cookie catches up.
 */
export function useExperienceMode(saved: TExperienceMode): IUseExperienceMode {
  const { i18n } = useLingui()
  const [current, setCurrent] = useState<TExperienceMode>(() => resolveExperienceMode(saved))

  useEffect(() => {
    setCurrent(resolveExperienceMode(saved))
    return subscribeExperienceMode(() => {
      setCurrent(resolveExperienceMode(saved))
    })
  }, [saved])

  const mutation = useMutation({
    mutationFn: (mode: TExperienceMode) => updateExperienceMode(mode),
    onMutate: (mode): { previous: TExperienceMode } => {
      const previous = current
      setCurrent(mode)
      setExperienceModeLocal(mode)
      return { previous }
    },
    onError: (err, _mode, ctx) => {
      if (ctx) {
        setCurrent(ctx.previous)
        setExperienceModeLocal(ctx.previous)
      }
      notifyError(err, i18n._(msg`Could not save mode`))
    },
    onSuccess: () => {
      void refreshSession()
    },
  })

  const setMode = useCallback(
    (mode: TExperienceMode): void => {
      if (mode === current) return
      mutation.mutate(mode)
    },
    [current, mutation],
  )

  return { current, setMode, isPending: mutation.isPending }
}
